import { useEffect, useState, type FormEvent } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import { ApiError } from '../../shared/api/client.ts';
import { useAlterarPropriaSenha } from '../../shared/api/queries.ts';
import { useSessao } from './sessao.tsx';

const TAMANHO_MINIMO = 8;
const VAZIO = { atual: '', nova: '', confirmacao: '' };

export function DialogoTrocarSenha({
  aberto,
  aoFechar,
}: {
  aberto: boolean;
  aoFechar: () => void;
}) {
  const { renovar } = useSessao();
  const [campos, setCampos] = useState(VAZIO);
  const alterar = useAlterarPropriaSenha();

  useEffect(() => {
    if (aberto) {
      setCampos(VAZIO);
      alterar.reset();
    }
    // Incluir a mutação aqui limparia o erro no mesmo render em que ele
    // aparece.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  const novaOk = campos.nova.length >= TAMANHO_MINIMO;
  const confere = campos.nova === campos.confirmacao;
  const podeEnviar = campos.atual !== '' && novaOk && confere && !alterar.isPending;

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (!podeEnviar) return;

    try {
      const sessao = await alterar.mutateAsync({
        senhaAtual: campos.atual,
        novaSenha: campos.nova,
      });
      // O servidor invalidou o token anterior; sem guardar o novo, a próxima
      // requisição cairia no login.
      renovar(sessao);
      aoFechar();
    } catch {
      // A mensagem está em `alterar.error`; o diálogo fica aberto.
    }
  }

  return (
    <Dialog
      open={aberto}
      onClose={alterar.isPending ? undefined : aoFechar}
      fullWidth
      maxWidth="xs"
      slotProps={{ paper: { component: 'form', onSubmit: enviar } }}
    >
      <DialogTitle>Trocar minha senha</DialogTitle>

      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <DialogContentText variant="body2">
            Ao trocar a senha, as sessões abertas em outros computadores são encerradas.
          </DialogContentText>

          {alterar.isError && <Alert severity="error">{mensagemDoErro(alterar.error)}</Alert>}

          <TextField
            label="Senha atual"
            type="password"
            value={campos.atual}
            onChange={(evento) => setCampos((atual) => ({ ...atual, atual: evento.target.value }))}
            required
            autoFocus
            fullWidth
            autoComplete="current-password"
          />

          <TextField
            label="Nova senha"
            type="password"
            value={campos.nova}
            onChange={(evento) => setCampos((atual) => ({ ...atual, nova: evento.target.value }))}
            required
            fullWidth
            autoComplete="new-password"
            error={campos.nova !== '' && !novaOk}
            helperText={`Ao menos ${TAMANHO_MINIMO} caracteres.`}
          />

          <TextField
            label="Repita a nova senha"
            type="password"
            value={campos.confirmacao}
            onChange={(evento) =>
              setCampos((atual) => ({ ...atual, confirmacao: evento.target.value }))
            }
            required
            fullWidth
            autoComplete="new-password"
            error={campos.confirmacao !== '' && !confere}
            helperText={campos.confirmacao !== '' && !confere ? 'As senhas não conferem.' : ' '}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={aoFechar} disabled={alterar.isPending}>
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!podeEnviar}
          loading={alterar.isPending}
        >
          Trocar senha
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ApiError) {
    return erro.status === 429
      ? 'Muitas tentativas. Espere um minuto e tente de novo.'
      : erro.message;
  }
  return 'Não foi possível trocar a senha.';
}
